import { imageSrc } from '@/lib/imageSrc';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Head from 'next/head';
import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { fontHandwritten } from './hero-with-mockup';

interface SlideImage {
  pc: string;
  tablet: string;
  mobile: string;
}

interface HeroSlide {
  _id: string;
  title: string;
  description: string;
  images: SlideImage;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface HeroSlidesProps {
  slides: HeroSlide[];
  autoPlay?: boolean;
  interval?: number;
  // SEO props
  pageTitle?: string;
  pageUrl?: string;
}

export const HeroSlides: React.FC<HeroSlidesProps> = ({
  slides,
  autoPlay = true,
  interval = 5000,
  pageTitle = 'Website Homepage',
  pageUrl = typeof window !== 'undefined' ? window.location.href : '',
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Auto-advance slides with smooth transition
  useEffect(() => {
    if (!isPlaying || slides.length <= 1) return;

    const timer = setTimeout(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setIsTransitioning(false);
      }, 500); // Match this with your CSS transition duration
    }, interval);

    return () => clearTimeout(timer);
  }, [currentSlide, isPlaying, interval, slides.length]);

  const goToSlide = (index: number) => {
    if (index === currentSlide) return;

    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsTransitioning(false);
      if (autoPlay) {
        setIsPlaying(false);
        setTimeout(() => setIsPlaying(true), interval * 2);
      }
    }, 200);
  };

  const goToPrevSlide = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
      setIsTransitioning(false);
      if (autoPlay) {
        setIsPlaying(false);
        setTimeout(() => setIsPlaying(true), interval * 2);
      }
    }, 200);
  };

  const goToNextSlide = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setIsTransitioning(false);
      if (autoPlay) {
        setIsPlaying(false);
        setTimeout(() => setIsPlaying(true), interval * 2);
      }
    }, 200);
  };

  if (!slides.length) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg bg-muted">
        <p className="text-muted-foreground">No slides available</p>
      </div>
    );
  }

  const currentSlideData = slides[currentSlide];

  return (
    <>
      {/* SEO Meta Tags */}
      <Head>
        <title>{currentSlideData?.title || pageTitle}</title>
        <meta
          name="description"
          content={currentSlideData?.description || ''}
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta
          property="og:title"
          content={currentSlideData?.title || pageTitle}
        />
        <meta
          property="og:description"
          content={currentSlideData?.description || ''}
        />
        <meta
          property="og:image"
          content={imageSrc(currentSlideData?.images.pc)}
        />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta
          property="twitter:title"
          content={currentSlideData?.title || pageTitle}
        />
        <meta
          property="twitter:description"
          content={currentSlideData?.description || ''}
        />
        <meta
          property="twitter:image"
          content={imageSrc(currentSlideData?.images.pc)}
        />
      </Head>

      <div className="group/icon relative z-10 h-[550px] w-full overflow-hidden md:h-[550px] lg:h-[600px] xl:h-[80vh]">
        {/* Slides container with transition effects */}
        <div className="relative h-full w-full">
          {slides.map((slide, index) => (
            <div
              key={slide._id}
              className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${
                index === currentSlide ? 'z-10 opacity-100' : 'z-0 opacity-0'
              } ${
                isTransitioning && index === currentSlide ? 'opacity-0' : ''
              }`}
            >
              {/* Responsive image with optimized loading */}
              <picture>
                <source
                  media="(min-width: 1024px)"
                  srcSet={imageSrc(slide.images.pc)}
                />
                <source
                  media="(min-width: 768px)"
                  srcSet={imageSrc(slide.images.tablet)}
                />
                <img
                  src={imageSrc(slide.images.mobile)}
                  alt={`${slide.title} - Best Physiotherapy Center in Dhaka`}
                  className="h-full w-full object-cover"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding={index === 0 ? 'sync' : 'async'}
                  fetchPriority={index === 0 ? 'high' : 'low'}
                />
              </picture>

              {/* Slide content overlay with fade-in effect */}
              {/* {bg - gradient - to - t from-black/70 via-black/30 to-transparent} */}
              <div
                className={`absolute inset-0 flex flex-col justify-end p-6 opacity-100 transition-opacity duration-300 md:p-8 ${
                  index === currentSlide && !isTransitioning
                    ? 'opacity-100'
                    : 'opacity-0'
                }`}
              >
                <div className="mx-auto max-w-3xl text-center">
                  <h5
                    className={cn(
                      'animate-fadeInUp mb-1 text-2xl font-bold text-white md:text-4xl',
                      fontHandwritten.className,
                    )}
                  >
                    {slide.title}
                  </h5>
                  <p className="animate-fadeInUp text-sm text-white/90 delay-100 md:text-base">
                    {slide.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation controls */}
        {slides.length > 1 && (
          <>
            <Button
              variant="outline"
              size="icon"
              className="absolute left-4 top-1/2 z-20 -translate-y-1/2 rounded-full border-background/40 bg-background/20 opacity-0 backdrop-blur-sm transition-all duration-500 hover:scale-110 hover:bg-background/40 group-hover/icon:opacity-100"
              onClick={goToPrevSlide}
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-5 w-5 text-gray-700" />
            </Button>

            <Button
              variant="outline"
              size="icon"
              className="absolute right-4 top-1/2 z-20 -translate-y-1/2 rounded-full border-background/40 bg-background/20 opacity-0 backdrop-blur-sm transition-all duration-500 hover:scale-110 hover:bg-background/40 group-hover/icon:opacity-100"
              onClick={goToNextSlide}
              aria-label="Next slide"
            >
              <ChevronRight className="h-5 w-5 text-gray-700" />
            </Button>

            <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2">
              {slides.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-2 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? 'scale-110 bg-primary'
                      : 'scale-100 bg-white/50 hover:bg-white/70'
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};
